/**
 * ConnectedUserResourceRegistry — UserResourceRegistry wired to the shared
 * userResourcesStore. Single wiring point reused by both hosts (Rails Phase 2):
 * the ResourceRegistryRail (wide screens) and the ResourcesPage in-page
 * fallback (narrow screens). Only one host mounts at a time (breakpoint-gated),
 * so there's exactly one registry instance and one source of truth.
 *
 * onResourcesChange is the array channel (the registry computes the next array
 * for add/edit/delete); onSave/onDelete persist to the API. This mirrors the
 * handlers ResourcesPage owned before the lift — behavior is unchanged.
 */
import React, { useCallback } from 'react';
import { UserResourceRegistry } from './UserResourceRegistry';
import { saveUserResource, deleteUserResource } from '../../api/userResources';
import { useUserResources } from '../../hooks/useUserResources';
import {
  setUserResources,
  setSelectedResourceGuid,
  setUserResourcesError,
} from '../../Data/UserResources/userResourcesStore';
import type { UserResource } from '../../Data/UserResources/userResourceTypes';

export function ConnectedUserResourceRegistry({
  currentUserGuid,
  hideHeader,
}: {
  currentUserGuid: string;
  hideHeader?: boolean;
}) {
  const { resources, selectedResourceGuid } = useUserResources();

  const handleSave = useCallback(async (resource: UserResource) => {
    try {
      const saved = await saveUserResource(resource);
      setUserResources((prev) =>
        prev.map((r) => (r.resource_guid === saved.resource_guid ? saved : r)),
      );
    } catch (error) {
      console.error('Failed to save resource:', error);
      setUserResourcesError(error instanceof Error ? error.message : 'Failed to save resource');
    }
  }, []);

  const handleDelete = useCallback(async (resourceGuid: string) => {
    try {
      await deleteUserResource(resourceGuid);
    } catch (error) {
      console.error('Failed to delete resource:', error);
      setUserResourcesError(error instanceof Error ? error.message : 'Failed to delete resource');
    }
  }, []);

  return (
    <UserResourceRegistry
      userGuid={currentUserGuid}
      resources={resources}
      onResourcesChange={setUserResources}
      onSave={handleSave}
      onDelete={handleDelete}
      onResourceSelect={(r) => setSelectedResourceGuid(r?.resource_guid || null)}
      selectedResourceGuid={selectedResourceGuid}
      hideHeader={hideHeader}
      canEdit
    />
  );
}

export default ConnectedUserResourceRegistry;
